import { useEffect, useRef } from 'react';
import { resolveFileVideoDriftCorrection, resolveFileVideoTarget } from '@/helpers/preview/synced-file-video.helper';

const DRIFT_CHECK_INTERVAL_MS = 1000;

const videoStyle: React.CSSProperties = {
  maxWidth: '100%',
  maxHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
  display: 'block',
};

/**
 * Live Current file video at the players' position: anchored on the take time stamped on the layer
 * (`playbackStartedAt`), with the same seek / rate drift correction the player uses.
 * The take time is server-clock; the local clock is assumed to be NTP-close to it.
 */
export function SyncedFileVideo({
  url,
  startedAtMs,
  loop = false,
  muted,
}: {
  url: string;
  startedAtMs?: number;
  loop?: boolean;
  muted: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    // Without a stamped take the clip counts from when Current first shows it.
    const anchorMs = startedAtMs ?? Date.now();
    let cancelled = false;

    const sync = () => {
      if (cancelled || video.readyState < HTMLMediaElement.HAVE_METADATA) {
        return;
      }
      const target = resolveFileVideoTarget({
        startedAtMs: anchorMs,
        nowMs: Date.now(),
        durationSec: video.duration,
        loop,
      });
      if (target.state === 'waiting') {
        video.pause();
        return;
      }
      if (target.state === 'ended') {
        if (Number.isFinite(video.duration) && video.currentTime !== video.duration) {
          video.currentTime = video.duration;
        }
        video.pause();
        return;
      }
      const correction = resolveFileVideoDriftCorrection(
        video.currentTime,
        target.positionSec,
        loop ? video.duration : undefined,
      );
      if (correction.kind === 'seek') {
        video.currentTime = correction.positionSec;
        video.playbackRate = 1;
      } else if (video.playbackRate !== correction.rate) {
        video.playbackRate = correction.rate;
      }
      if (video.paused) {
        void video.play().catch(() => {
          // Unmuted autoplay needs a gesture; keep the picture moving muted.
          if (!cancelled && !video.muted) {
            video.muted = true;
            void video.play().catch(() => {});
          }
        });
      }
    };

    video.addEventListener('loadedmetadata', sync);
    video.addEventListener('canplay', sync);
    sync();
    const intervalId = window.setInterval(sync, DRIFT_CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      video.removeEventListener('loadedmetadata', sync);
      video.removeEventListener('canplay', sync);
      video.playbackRate = 1;
    };
  }, [url, startedAtMs, loop]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted;
    }
  }, [muted]);

  return (
    <video
      ref={videoRef}
      key={url}
      src={url}
      muted={muted}
      playsInline
      preload="auto"
      controls={false}
      loop={loop}
      style={videoStyle}
    />
  );
}
